import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';

// Types
export interface CurrencyBalance {
  currency: number;
  premiumCurrency: number;
  userId: number;
}

export interface Transaction {
  id: number;
  userId: number;
  amount: number;
  currencyType: 'regular' | 'premium';
  transactionType: 'purchase' | 'reward' | 'admin' | 'system';
  description: string;
  itemId?: number;
  createdAt: string;
}

export interface ShopItem {
  id: number;
  name: string;
  description: string;
  imageUrl?: string;
  audioUrl?: string;
  soundType?: string;
  price: number;
  currencyType: 'regular' | 'premium';
  itemType: string;
  category: string;
  isActive: boolean;
  managerMode: boolean;
  stock: number | null;
}

export interface UserPurchase {
  id: number;
  userId: number;
  itemId: number;
  purchaseDate: string;
  expiryDate?: string;
  isActive: boolean;
}

export interface PurchaseWithItem {
  purchase: UserPurchase;
  item: ShopItem;
}

export interface DailyRewardResponse {
  success: boolean;
  reward?: {
    amount: number;
    type: 'regular' | 'premium';
  };
  nextRewardTime?: string;
}

// Hooks for fetching data
export function useCurrencyBalance() {
  return useQuery({ 
    queryKey: ['/api/currency/balance'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/currency/balance');
      const data = await response.json();
      console.log('API response for balance:', data);
      
      if (!data) {
        return { currency: 0, premiumCurrency: 0 };
      }
      
      // Ensure we return an object with currency and premiumCurrency
      return {
        currency: data.currency !== undefined ? data.currency : 0,
        premiumCurrency: data.premiumCurrency !== undefined ? data.premiumCurrency : 0
      };
    }
  });
}

export function useTransactions(limit?: number) {
  return useQuery({ 
    queryKey: ['/api/currency/transactions', { limit }],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/currency/transactions${limit ? `?limit=${limit}` : ''}`);
      return response.json();
    },
  });
}

export function useShopItems() {
  return useQuery({ 
    queryKey: ['/api/currency/shop'],
    queryFn: async () => {
      try {
        console.log('Fetching shop items...');
        const response = await apiRequest('GET', '/api/currency/shop');
        
        // Log the raw response for debugging
        console.log('Shop API response status:', response.status);
        console.log('Shop API response headers:', response.headers);
        
        // Parse the response as JSON
        const data = await response.json();
        console.log('API response for shop items:', data);
        console.log('Data type:', typeof data);
        console.log('Is array:', Array.isArray(data));
        
        // Ensure we always return an array, no matter what structure the server sends
        if (Array.isArray(data)) {
          console.log('Returning array with length:', data.length);
          return data;
        } else if (data && typeof data === 'object') {
          const values = Object.values(data) as ShopItem[];
          console.log('Converting object to array with length:', values.length);
          console.log('First item sample:', values.length > 0 ? values[0] : 'No items');
          return values;
        } else {
          console.error('Unexpected shop data format:', data);
          return [];
        }
      } catch (error) {
        console.error('Error fetching shop items:', error);
        throw error;
      }
    }
  });
}

export interface PendingTransaction {
  id: number;
  userId: number;
  reference: string;
  amount: number;
  coins: number;
  coinType: 'regular' | 'premium';
  status: 'pending' | 'completed' | 'failed';
  email: string;
  metadata?: string;
  paymentDetails?: string;
  createdAt: string;
  updatedAt: string;
}

export function usePendingTransactions() {
  const queryClient = useQueryClient();
  const verifyPaymentMutation = useMutation({
    mutationFn: async (reference: string) => {
      const response = await apiRequest('GET', `/api/currency/paystack/verify?reference=${reference}`);
      return response;
    }
  });

  return useQuery({ 
    queryKey: ['/api/currency/pending-transactions'],
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', '/api/currency/pending-transactions');
        const data = await response.json();
        console.log('API response for pending transactions:', data);
        
        // Get the pending transactions array
        let transactions = [];
        if (data && data.pendingTransactions && Array.isArray(data.pendingTransactions)) {
          transactions = data.pendingTransactions;
        } else if (Array.isArray(data)) {
          transactions = data;
        } else if (data && typeof data === 'object') {
          transactions = Object.values(data);
        }

        // Verify any pending transactions in the background
        transactions.forEach(async (transaction: PendingTransaction) => {
          if (transaction.status === 'pending') {
            try {
              const result = await verifyPaymentMutation.mutateAsync(transaction.reference);
              if (result.success) {
                // Update the cache with the new status
                queryClient.setQueryData(['/api/currency/pending-transactions'], (oldData: any) => {
                  const transactions = oldData || [];
                  return transactions.map((t: PendingTransaction) => 
                    t.reference === transaction.reference 
                      ? { ...t, status: 'completed' }
                      : t
                  );
                });
              } else {
                // Update the cache to mark as failed
                queryClient.setQueryData(['/api/currency/pending-transactions'], (oldData: any) => {
                  const transactions = oldData || [];
                  return transactions.map((t: PendingTransaction) => 
                    t.reference === transaction.reference 
                      ? { ...t, status: 'failed' }
                      : t
                  );
                });
              }
            } catch (error) {
              console.error('Error verifying transaction:', error);
            }
          }
        });
        
        return transactions;
      } catch (error) {
        console.error('Error fetching pending transactions:', error);
        // Return an empty array on error rather than throwing
        return [];
      }
    },
    // Set a default value if the query fails or returns undefined
    select: (data) => {
      return data || [];
    },
    // Refresh every 30 seconds to catch any status changes
    refetchInterval: 30000
  });
}

export function usePurchases() {
  return useQuery({ 
    queryKey: ['/api/currency/purchases'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/currency/purchases');
      const data = await response.json();
      console.log('API response for purchases:', data);
      
      // Normalize data to an array first
      let purchases = [];
      
      // Check if data is an array
      if (Array.isArray(data)) {
        purchases = data;
      } 
      // If data is an object with numeric keys, convert it to an array
      else if (data && typeof data === 'object') {
        purchases = Object.values(data);
      } else {
        console.error('Unexpected purchases data format:', data);
        return [];
      }

      // Now transform the data into the expected format for the component
      return purchases.map(purchase => {
        // Format expected by UserPurchases component
        return {
          purchase: {
            id: purchase.id,
            userId: purchase.userId,
            itemId: purchase.shopItemId || purchase.itemId,
            purchaseDate: purchase.purchaseDate,
            expiryDate: purchase.expiryDate,
            isActive: purchase.isActive === false ? false : true // Default to true if undefined
          },
          item: purchase.item || {
            id: purchase.shopItemId || purchase.itemId,
            name: purchase.itemName || "Item",
            description: purchase.itemDescription || "",
            imageUrl: purchase.itemImageUrl || null
          }
        };
      });
    }
  });
}

// Mutations for modifying data
export function useClaimDailyReward() {
  return useMutation({
    mutationFn: () => apiRequest('POST', '/api/currency/daily-rewards/claim'),
    onSuccess: (data) => {
      console.log('Daily reward claimed:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      return data;
    }
  });
}

export function useClaimGameReward() {
  return useMutation({
    mutationFn: (gameId: number) => apiRequest('POST', '/api/currency/game-rewards/claim', {
      gameId
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/game-rewards'] });
    }
  });
}

export interface AchievementReward {
  id: number;
  name: string;
  description: string;
  category: string;
  requirement: number;
  imageUrl: string;
  progress: number;
  completed: boolean;
  claimed: boolean;
  reward: {
    amount: number;
    currencyType: 'regular' | 'premium';
  };
}

export interface GameVictoryReward {
  id: number;
  gameId: number;
  gameName: string;
  date: string;
  reward: {
    amount: number;
    currencyType: 'regular' | 'premium';
  };
  claimed: boolean;
}

export function useAchievementRewards(limit?: number) {
  const query = useQuery({ 
    queryKey: ['/api/currency/achievement-rewards', { limit }],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/currency/achievement-rewards${limit ? `?limit=${limit}` : ''}`);
      const data = await response.json();
      console.log('Achievement rewards data:', data);
      return data;
    }
  });
  
  const claimAchievementReward = useMutation({
    mutationFn: (achievementId: number) => apiRequest('POST', '/api/currency/achievement-rewards/claim', { 
      achievementId 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/achievement-rewards'] });
    }
  });
  
  return {
    ...query,
    claimAchievementReward
  };
}

export function useGameVictoryRewards(limit?: number) {
  const query = useQuery({ 
    queryKey: ['/api/currency/game-rewards', { limit }],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/currency/game-rewards${limit ? `?limit=${limit}` : ''}`);
      const data = await response.json();
      console.log('Game rewards data:', data);
      return data;
    }
  });
  
  const claimGameReward = useMutation({
    mutationFn: (gameId: number) => apiRequest('POST', '/api/currency/game-rewards/claim', { 
      gameId 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/game-rewards'] });
    }
  });
  
  return {
    ...query,
    claimGameReward
  };
}

export function useClaimAchievementReward() {
  return useMutation({
    mutationFn: (achievementId: number) => apiRequest('POST', '/api/currency/achievement-rewards/claim', { 
      achievementId 
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/achievement-rewards'] });
    }
  });
}

export function useAddTransaction() {
  return useMutation({
    mutationFn: (transaction: {
      amount: number;
      type: 'regular' | 'premium';
      transactionType: 'purchase' | 'reward' | 'admin' | 'system';
      description: string;
      itemId?: number;
    }) => apiRequest('POST', '/api/currency/transaction', transaction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
    }
  });
}

export function useAdminAddCurrency() {
  return useMutation({
    mutationFn: (data: {
      userId: number;
      amount: number;
      type: 'regular' | 'premium';
      transactionType?: 'reward' | 'admin' | 'system';
      description?: string;
    }) => apiRequest('POST', '/api/currency/admin/add', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
    }
  });
}

export function usePurchaseItem() {
  return useMutation({
    mutationFn: async (itemId: number) => {
      try {
        console.log(`Attempting to purchase item ${itemId}...`);
        const response = await apiRequest('POST', `/api/currency/shop/${itemId}/purchase`);
        
        // Log response details
        console.log('Purchase response status:', response.status);
        
        // Parse the response
        const data = await response.json();
        console.log('Purchase response data:', data);
        
        return data;
      } catch (error) {
        console.error('Error purchasing item:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('Purchase successful:', data);
      queryClient.invalidateQueries({ queryKey: ['/api/currency/balance'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/transactions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/purchases'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/shop'] });
    },
    onError: (error) => {
      console.error('Purchase mutation error:', error);
    }
  });
}

export function useCreateShopItem() {
  return useMutation({
    mutationFn: (item: {
      name: string;
      description: string;
      imageUrl?: string;
      price: number;
      currencyType: 'regular' | 'premium';
      itemType: string;
      isActive?: boolean;
      stock?: number;
    }) => apiRequest('POST', '/api/currency/admin/shop', item),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/shop'] });
    }
  });
}

export function useUpdateShopItem() {
  return useMutation({
    mutationFn: ({ id, data }: { 
      id: number, 
      data: Partial<{
        name: string;
        description: string;
        imageUrl: string;
        price: number;
        currencyType: 'regular' | 'premium';
        itemType: string;
        isActive: boolean;
        stock: number;
      }>
    }) => apiRequest('PATCH', `/api/currency/admin/shop/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/currency/shop'] });
      queryClient.invalidateQueries({ queryKey: ['/api/currency/purchases'] });
    }
  });
}